import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy: React.FC = () => {
    const location = useLocation();
    const isFromSignup = new URLSearchParams(location.search).get('source') === 'signup';

    return (
        <div className="min-h-screen bg-finance-darker py-20 px-4">
            <Helmet>
                <title>Privacy Policy — ForexFundAI</title>
                <meta name="description" content="Read the ForexFund AI Privacy Policy. Learn how we collect, process, and protect your institutional trading data and personal information in our clear disclosure." />
                <link rel="canonical" href="https://www.forexfundai.com/privacy" />
            </Helmet>
            <div className="max-w-4xl mx-auto bg-finance-card border border-gray-800 rounded-3xl p-8 sm:p-12 shadow-2xl">
                <h1 className="text-4xl font-black text-white mb-8">Privacy Policy</h1>
                <p className="text-gray-400 mb-6 text-sm italic">Last Updated: March 8, 2026</p>

                <div className="space-y-8 text-gray-300 leading-relaxed mb-12">
                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">1. Data Collection & Analytics</h2>
                        <p>
                            We collect information you provide directly to us (e.g., email, name, payment requests) and information collected automatically through our Service. This includes usage data collected via <strong>Google Analytics</strong> and other performance monitoring tools to improve our technical scanners.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">2. Use of Information</h2>
                        <p>
                            Collected data is used to provide, maintain, and improve our institutional analysis tools, process transactions via secure payment gateways, send technical signals, and communicate about new features.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">3. Data Sharing & Payment Providers</h2>
                        <p>
                            We do not sell your personal data. We share data only with trusted service providers necessary to operate the Service. This includes <strong>Supabase</strong> (Database & Auth), <strong>Brevo</strong> (Communication), and our <strong>Payment Gateway Providers</strong> (NOWPayments) for secure transaction processing. These third parties are obligated to protect your data according to their own strict privacy standards.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">4. Data Security</h2>
                        <p>
                            We implement robust security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">5. Cookies</h2>
                        <p>
                            We use cookies to enhance your experience, remember preferences, and analyze site traffic. You can manage cookie settings through your browser.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold text-white mb-4">6. Your Rights</h2>
                        <p>
                            Depending on your location, you may have the right to access, correct, or delete your personal data. Contact us at support@forexfund.ai for any privacy-related requests.
                        </p>
                    </section>
                </div>

                <div className="pt-8 border-t border-gray-800 flex justify-center">
                    <Link
                        to={isFromSignup ? "/login?view=signup" : "/"}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white font-bold hover:bg-gray-800 hover:border-gray-700 transition-all group"
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        {isFromSignup ? "Back to Sign Up" : "Back to Home"}
                    </Link>
                </div>
            </div>
        </div>
    );
};
